import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';
import { LoadingSpinner, PageError } from '@/components/ui';
import { useTranslation } from 'react-i18next';

export default function OilPage() {
  const apiClient = useApiClient();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();

  const [selectedStationId, setSelectedStationId] = useState<string | null>(null);
  const [method, setMethod] = useState('Sonde');
  const [polarity, setPolarity] = useState(0);
  const [visualQuality, setVisualQuality] = useState<'GOOD' | 'BAD' | null>(null);

  const isVisual = method === 'Visuel';

  const REFRESH_INTERVAL = 30 * 1000;

  const METHODS = [
    { value: 'Sonde', label: t('oil.methodProbe') },
    { value: 'Languette', label: t('oil.methodStrip') },
    { value: 'Visuel', label: t('oil.methodVisual') },
  ];

  const { data: stationsDataRaw, isLoading: isLoadingStations, error: stationsError } = useQuery({
    queryKey: ['oil-stations'],
    queryFn: async () => {
      const result = await apiClient.getOilStations();
      const stations = result?.stations || result?.data?.stations || [];
      return Array.isArray(stations) ? stations : [];
    },
    refetchInterval: REFRESH_INTERVAL,
  });

  const stationsData = Array.isArray(stationsDataRaw) ? stationsDataRaw : [];

  const { data: checkHistory, isLoading: isLoadingHistory } = useQuery({
    queryKey: ['oil-checks', selectedStationId],
    queryFn: async () => {
      if (!selectedStationId) return [];
      const result = await apiClient.getOilCheckHistory(selectedStationId);
      return Array.isArray(result) ? result : [];
    },
    enabled: !!selectedStationId,
    refetchInterval: REFRESH_INTERVAL,
  });

  const recordCheckMutation = useMutation({
    mutationFn: async () => {
      if (!selectedStationId) throw new Error(t('oil.noStationSelected'));
      if (isVisual) {
        if (!visualQuality) throw new Error(t('oil.specifyQuality'));
        return apiClient.recordOilCheck(selectedStationId, visualQuality, false, method, undefined, undefined);
      }
      const quality = polarity <= 25 ? 'GOOD' : 'BAD';
      return apiClient.recordOilCheck(selectedStationId, quality, false, method, undefined, polarity);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['oil-stations'] });
      queryClient.invalidateQueries({ queryKey: ['oil-checks', selectedStationId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setPolarity(0);
      setVisualQuality(null);
      addToast(t('oil.recorded'), 'success');
    },
    onError: (error: any) => {
      addToast(error?.response?.data?.message || t('oil.recordError'), 'error');
    },
  });

  const selectedStation = stationsData.find((s: any) => s.id === selectedStationId);

  const getPolarityColor = (val: number) => {
    if (val <= 15) return 'text-accent-green';
    if (val <= 25) return 'text-accent-orange';
    return 'text-accent-red';
  };

  const getPolarityLabel = (val: number) => {
    if (val <= 15) return t('oil.excellent');
    if (val <= 25) return t('oil.acceptable');
    return t('oil.mustChange');
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6 sm:mb-8 flex items-center gap-4 animate-slide-in">
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg">
          <span className="text-2xl">🍟</span>
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-brand-dark to-primary-600 bg-clip-text text-transparent">{t('oil.title')}</h1>
          <p className="text-neutral-500 mt-1 text-sm font-medium">{t('oil.subtitle')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-gradient-to-br from-white via-neutral-50 to-neutral-100 rounded-xl border border-neutral-200 p-5 shadow-card hover:shadow-card-hover transition-all duration-300">
            <div className="flex items-center gap-2 mb-4">
              <h2 className="text-base font-bold text-neutral-900">{t('oil.fryer')}</h2>
              <span className="text-accent-red">*</span>
              <div className="flex-1 h-px bg-primary-500 ml-2" />
            </div>

            {isLoadingStations ? (
              <LoadingSpinner message={t('oil.loadingFryers')} size="sm" className="py-4" />
            ) : stationsError ? (
              <PageError message={t('oil.loadingFryers')} />
            ) : stationsData.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-neutral-500 mb-2">{t('oil.noFryers')}</p>
                <a href="/equipment" className="text-primary-600 hover:text-primary-700 hover:underline text-sm font-medium">
                  {t('oil.addToEquipment')}
                </a>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {stationsData.map((station: any) => (
                  <button
                    key={station.id}
                    onClick={() => setSelectedStationId(station.id)}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all duration-300 text-left ${
                      selectedStationId === station.id
                        ? 'border-primary-500 bg-primary-50 shadow-md'
                        : 'border-neutral-200 hover:border-primary-300 hover:bg-primary-50/30'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      selectedStationId === station.id ? 'bg-primary-100' : 'bg-neutral-100'
                    }`}>
                      <span className="text-lg">🍟</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-neutral-900 truncate">{station.name}</div>
                      <div className="text-xs text-neutral-500">{t('oil.fryer')}</div>
                    </div>
                    {station.lastCheck && (
                      <div className={`text-xs font-semibold px-2 py-1 rounded-full ${
                        station.lastCheck.quality === 'GOOD'
                          ? 'bg-accent-green/10 text-accent-green'
                          : 'bg-accent-red/10 text-accent-red'
                      }`}>
                        {station.lastCheck.polarity != null ? `${station.lastCheck.polarity}%` : (station.lastCheck.quality === 'GOOD' ? 'OK' : '!')}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {selectedStation && (
            <>
              <div className="bg-gradient-to-br from-white via-neutral-50 to-neutral-100 rounded-xl border border-neutral-200 p-5 shadow-card hover:shadow-card-hover transition-all duration-300">
                <div className="flex items-center gap-2 mb-4">
                  <h2 className="text-base font-bold text-neutral-900">{t('oil.method')}</h2>
                  <span className="text-accent-red">*</span>
                  <div className="flex-1 h-px bg-primary-500 ml-2" />
                </div>

                <div className="flex flex-wrap gap-3">
                  {METHODS.map((m) => (
                    <button
                      key={m.value}
                      onClick={() => setMethod(m.value)}
                      className={`px-5 py-3 rounded-lg font-semibold text-sm transition-all duration-300 border-2 ${
                        method === m.value
                          ? 'bg-gradient-to-r from-primary-600 to-primary-500 text-white border-primary-500 shadow-md shadow-primary-500/30'
                          : 'bg-white text-neutral-600 border-neutral-200 hover:border-primary-300'
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>

              {!isVisual && (
                <div className="bg-gradient-to-br from-white via-neutral-50 to-neutral-100 rounded-xl border border-neutral-200 p-5 shadow-card hover:shadow-card-hover transition-all duration-300">
                  <div className="flex items-center gap-2 mb-4">
                    <h2 className="text-base font-bold text-neutral-900">{t('oil.polarity')}</h2>
                    <span className="text-accent-red">*</span>
                    <div className="flex-1 h-px bg-primary-500 ml-2" />
                  </div>

                  <div className="flex items-center justify-center gap-4">
                    <button
                      onClick={() => setPolarity(Math.max(0, polarity - 1))}
                      className="w-16 h-14 rounded-lg bg-neutral-100 hover:bg-neutral-200 text-neutral-700 text-2xl font-bold transition-colors flex items-center justify-center"
                    >
                      −
                    </button>
                    <div className="w-24 h-14 rounded-lg border-2 border-primary-400 bg-primary-50 flex items-center justify-center">
                      <span className={`text-2xl font-bold ${getPolarityColor(polarity)}`}>{polarity}</span>
                    </div>
                    <button
                      onClick={() => setPolarity(Math.min(100, polarity + 1))}
                      className="w-16 h-14 rounded-lg bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 text-white text-2xl font-bold transition-all duration-300 flex items-center justify-center shadow-md shadow-primary-500/30"
                    >
                      +
                    </button>
                  </div>

                  <div className="mt-3 text-center">
                    <span className={`text-sm font-medium ${getPolarityColor(polarity)}`}>
                      {getPolarityLabel(polarity)}
                    </span>
                  </div>
                </div>
              )}

              {isVisual && (
                <div className="bg-gradient-to-br from-white via-neutral-50 to-neutral-100 rounded-xl border border-neutral-200 p-5 shadow-card hover:shadow-card-hover transition-all duration-300">
                  <div className="flex items-center gap-2 mb-4">
                    <h2 className="text-base font-bold text-neutral-900">{t('oil.oilQuality')}</h2>
                    <span className="text-accent-red">*</span>
                    <div className="flex-1 h-px bg-primary-500 ml-2" />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => setVisualQuality('GOOD')}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all duration-300 ${
                        visualQuality === 'GOOD'
                          ? 'border-accent-green bg-accent-green/10 shadow-md'
                          : 'border-neutral-200 hover:border-accent-green/50'
                      }`}
                    >
                      <span className="text-3xl">✓</span>
                      <span className={`font-semibold ${
                        visualQuality === 'GOOD' ? 'text-accent-green' : 'text-neutral-600'
                      }`}>{t('oil.good')}</span>
                    </button>
                    <button
                      onClick={() => setVisualQuality('BAD')}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all duration-300 ${
                        visualQuality === 'BAD'
                          ? 'border-accent-red bg-accent-red/10 shadow-md'
                          : 'border-neutral-200 hover:border-accent-red/50'
                      }`}
                    >
                      <span className="text-3xl">✗</span>
                      <span className={`font-semibold ${
                        visualQuality === 'BAD' ? 'text-accent-red' : 'text-neutral-600'
                      }`}>{t('oil.bad')}</span>
                    </button>
                  </div>
                </div>
              )}

              <button
                onClick={() => recordCheckMutation.mutate()}
                disabled={recordCheckMutation.isPending || (isVisual && !visualQuality)}
                className="w-full py-4 rounded-xl bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-700 hover:to-primary-600 text-white text-lg font-bold transition-all duration-300 disabled:opacity-50 shadow-lg shadow-primary-500/30 hover:shadow-xl hover:scale-105"
              >
                {recordCheckMutation.isPending ? t('oil.saving') : t('oil.validateBtn')}
              </button>
            </>
          )}
        </div>

        <div className="lg:col-span-1">
          <div className="bg-gradient-to-br from-white via-neutral-50 to-neutral-100 rounded-xl border border-neutral-200 p-5 sticky top-6 shadow-card">
            <div className="flex items-center gap-2 mb-4">
              <h2 className="text-base font-bold text-neutral-900">{t('oil.history')}</h2>
              <div className="flex-1 h-px bg-primary-500 ml-2" />
            </div>

            {!selectedStationId ? (
              <p className="text-neutral-400 text-sm text-center py-6">
                {t('oil.selectFryer')}
              </p>
            ) : isLoadingHistory ? (
              <p className="text-neutral-400 text-sm text-center py-6">{t('oil.loading')}</p>
            ) : checkHistory?.length === 0 ? (
              <p className="text-neutral-400 text-sm text-center py-6">{t('oil.noChecks')}</p>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {checkHistory?.slice(0, 15).map((check: any) => (
                  <div
                    key={check.id}
                    className="p-3 rounded-lg border border-neutral-100 bg-neutral-50"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className={`inline-block w-2.5 h-2.5 rounded-full ${
                          check.quality === 'GOOD' ? 'bg-accent-green' : 'bg-accent-red'
                        }`} />
                        <span className="text-sm font-semibold text-neutral-900">
                          {check.polarity != null ? `${check.polarity}%` : check.quality}
                        </span>
                      </div>
                      <span className="text-xs text-neutral-400">
                        {new Date(check.createdAt).toLocaleDateString(i18n.language, {
                          day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <div className="text-xs text-neutral-500">{check.method}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
